// =========================================
// auth.firebase.js — autenticazione su Firebase Auth + profilo su Firestore
// =========================================
//
// Espone la STESSA superficie di auth.local.js. L'identità è gestita da
// Firebase Auth (email/password, verifica email, reset password); i dati di
// profilo estesi (tenantId, ruolo, dati fatturazione, preferenze) vivono in
// Firestore sotto  users/{uid}.
//
// Interfaccia sincrona: `getCurrentUser()` legge una cache in memoria
// (`profileCache`) mantenuta allineata da onAuthStateChanged + onSnapshot su
// users/{uid}. `onAuthChange` notifica i consumer ad ogni variazione.

import {
  onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, sendEmailVerification, sendPasswordResetEmail, updateProfile, deleteUser,
} from 'firebase/auth';
import {
  doc, getDoc, onSnapshot, Timestamp,
  setDoc as fbSetDoc, updateDoc as fbUpdateDoc, deleteDoc as fbDeleteDoc,
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { TRIAL_MS, trialEndsMillis } from '../data/trial';

const listeners = new Set();

let profileCache = null;       // publicUser | null
let profileRaw = {};           // ultimo snapshot doc users/{uid}
let authResolved = false;      // true dopo il primo onAuthStateChanged
let authListenerAttached = false;
let profileUnsub = null;

const slugify = (s) => String(s).toLowerCase().trim()
  .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');

function emit() {
  const u = getCurrentUser();
  listeners.forEach(fn => { try { fn(u); } catch { /* ignore */ } });
}

// Forma pubblica dell'utente (identica a quella dell'auth locale).
function mergeProfile(fbUser, data = {}) {
  return {
    uid: fbUser.uid,
    email: fbUser.email || data.email || '',
    displayName: data.displayName || fbUser.displayName || '',
    nome: data.nome || '',
    cognome: data.cognome || '',
    phone: data.phone || '',
    emailVerified: !!fbUser.emailVerified, // fonte di verità: Firebase Auth
    tenantId: data.tenantId || null,
    tenantName: data.tenantName || '',
    role: data.role || 'titolare',
    taxInfo: data.taxInfo || {},
    notificationPrefs: data.notificationPrefs || {},
    // Scadenza prova in millisecondi (null = nessun trial → mai scaduto).
    trialEndsAt: trialEndsMillis(data.trialEndsAt),
  };
}

// Attacca (una sola volta) il listener globale sullo stato di autenticazione.
function ensureAuthListener() {
  const a = auth();
  if (!a || authListenerAttached) return;
  authListenerAttached = true;
  onAuthStateChanged(a, (fbUser) => {
    authResolved = true;
    if (profileUnsub) { profileUnsub(); profileUnsub = null; }
    if (!fbUser) { profileCache = null; profileRaw = {}; emit(); return; }
    // Segui il doc di profilo live.
    const ref = doc(db(), 'users', fbUser.uid);
    profileUnsub = onSnapshot(
      ref,
      (snap) => {
        profileRaw = snap.exists() ? snap.data() : {};
        profileCache = mergeProfile(fbUser, profileRaw);
        emit();
      },
      () => { profileCache = mergeProfile(fbUser, profileRaw); emit(); },
    );
  });
}

// ── API pubblica ──────────────────────────────────────────────────────────────

export function getCurrentUser() {
  return profileCache;
}

// true finché Firebase non ha risolto lo stato iniziale (per evitare che la UI
// tratti come "non autenticato" un utente ancora in fase di ripristino sessione).
export function authPending() {
  return !authResolved;
}

export function onAuthChange(fn) {
  ensureAuthListener();
  listeners.add(fn);
  if (authResolved) Promise.resolve().then(() => fn(getCurrentUser()));
  return () => listeners.delete(fn);
}

export async function registerUser({ email, password, displayName }) {
  ensureAuthListener();
  const key = email.trim().toLowerCase();
  const cred = await createUserWithEmailAndPassword(auth(), key, password);
  if (displayName) { try { await updateProfile(cred.user, { displayName }); } catch { /* non bloccante */ } }
  profileRaw = {
    uid: cred.user.uid, email: key, displayName: displayName || '',
    nome: '', cognome: '', phone: '', tenantId: null, tenantName: '',
    role: 'titolare', taxInfo: {}, notificationPrefs: {}, createdAt: Date.now(),
    // Scadenza prova: Timestamp Firestore (modificabile a mano dalla console).
    trialEndsAt: Timestamp.fromMillis(Date.now() + TRIAL_MS),
  };
  await fbSetDoc(doc(db(), 'users', cred.user.uid), profileRaw, { merge: true });
  // NB: l'email di verifica NON viene inviata qui. Ci pensa la pagina register
  // con un'unica chiamata a sendVerificationEmail() — evita il doppio invio che
  // porterebbe rapidamente a auth/too-many-requests (come nel flusso dashboard).
  profileCache = mergeProfile(cred.user, profileRaw);
  emit();
  return profileCache;
}

export async function loginUser({ email, password }) {
  ensureAuthListener();
  const key = email.trim().toLowerCase();
  const cred = await signInWithEmailAndPassword(auth(), key, password);
  const snap = await getDoc(doc(db(), 'users', cred.user.uid));
  profileRaw = snap.exists() ? snap.data() : {};
  profileCache = mergeProfile(cred.user, profileRaw);
  emit();
  return profileCache;
}

export async function logoutUser() {
  await signOut(auth()); // onAuthStateChanged azzera la cache + emit
}

export async function sendVerificationEmail() {
  const u = auth()?.currentUser;
  if (!u) { const e = new Error('Nessun utente attivo.'); e.code = 'auth/no-current-user'; throw e; }
  await sendEmailVerification(u);
  return true;
}

// Con Firebase la verifica avviene cliccando il link nell'email: qui ricarichiamo
// lo stato dell'utente e, se verificato, lo riflettiamo su Firestore. Ritorna
// true solo se la verifica risulta effettivamente completata.
export async function markEmailVerified() {
  const u = auth()?.currentUser;
  if (!u) { const e = new Error('Nessun utente attivo.'); e.code = 'auth/no-current-user'; throw e; }
  await u.reload();
  if (u.emailVerified) {
    // Forza il refresh dell'ID token: il claim `email_verified` diventa true nel
    // token usato dalle richieste Firestore successive (altrimenti le regole
    // negherebbero i dati del tenant subito dopo la verifica, con token stale).
    await u.getIdToken(true);
    try { await fbUpdateDoc(doc(db(), 'users', u.uid), { emailVerified: true }); } catch { /* opzionale */ }
    profileCache = mergeProfile(u, profileRaw);
    emit();
    return true;
  }
  return false;
}

export async function sendPasswordReset(email) {
  await sendPasswordResetEmail(auth(), email.trim().toLowerCase());
  return true;
}

// Associa un tenant all'utente corrente. Aggiorna la cache in modo ottimistico
// e RITORNA la promise della scrittura: chi crea il tenant deve poterla attendere
// perché le regole Firestore consentono di scrivere i dati del tenant solo dopo
// che users/{uid}.tenantId è stato impostato.
export function attachTenantToCurrentUser(tenantId, tenantName) {
  const u = auth()?.currentUser;
  if (!u) return Promise.resolve();
  profileRaw = { ...profileRaw, tenantId, tenantName };
  if (profileCache) profileCache = { ...profileCache, tenantId, tenantName };
  emit();
  return fbUpdateDoc(doc(db(), 'users', u.uid), { tenantId, tenantName })
    .catch((err) => console.error('[auth] attachTenant fallita:', err?.code || err));
}

export function updateCurrentUserProfile(patch = {}) {
  const u = auth()?.currentUser;
  if (!u) return;
  const FIELDS = ['displayName', 'nome', 'cognome', 'phone', 'taxInfo', 'notificationPrefs'];
  const clean = {};
  for (const k of FIELDS) if (patch[k] != null) clean[k] = patch[k];
  if (!Object.keys(clean).length) return;
  profileRaw = { ...profileRaw, ...clean };
  if (profileCache) profileCache = mergeProfile(u, profileRaw);
  emit();
  fbUpdateDoc(doc(db(), 'users', u.uid), clean)
    .catch((err) => console.error('[auth] updateProfile fallita:', err?.code || err));
}

// Elimina il profilo e l'utente Auth. NB: in produzione la Cloud Function
// deleteAccount fa il lavoro completo (tenant inclusi); qui è il best-effort
// client per la parità con il flusso locale.
export async function deleteCurrentAccount() {
  const u = auth()?.currentUser;
  if (!u) return;
  try { await fbDeleteDoc(doc(db(), 'users', u.uid)); } catch { /* ignora */ }
  try { await deleteUser(u); } catch (err) {
    // auth/requires-recent-login: serve ri-login recente per cancellare l'utente.
    console.error('[auth] deleteUser fallita:', err?.code || err);
  }
}

export { slugify };
